import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GUIDE_CATEGORIES, guideEditorApi } from '../../services/guide-editor-api';

// The Content API's limits on the new-guide fields.
const MAX_CITY = 80;
const MAX_TITLE = 200;

/** "New guide": city, category and title, then straight into the editor. */
export default function NewGuideDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [city, setCity] = useState('');
  const [category, setCategory] = useState<string>(GUIDE_CATEGORIES[0].value);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCity('');
      setCategory(GUIDE_CATEGORIES[0].value);
      setTitle('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const valid =
    city.trim().length > 0 &&
    city.length <= MAX_CITY &&
    title.trim().length > 0 &&
    title.length <= MAX_TITLE;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const made = await guideEditorApi.createGuide({
        city: city.trim(),
        category,
        title: title.trim(),
      });
      onClose();
      navigate(`/guides/${made.slug}/edit`);
    } catch (err: any) {
      setError(err?.message || 'The guide could not be created');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New guide</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This creates a draft with one empty chapter, written by you. Add the places next.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
            autoFocus
            size="small"
            helperText={`${city.length}/${MAX_CITY}`}
            error={city.length > MAX_CITY}
            inputProps={{ 'data-testid': 'new-guide-city' }}
          />
          <FormControl size="small">
            <InputLabel id="new-guide-category">Category</InputLabel>
            <Select
              labelId="new-guide-category"
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {GUIDE_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            size="small"
            helperText={`${title.length}/${MAX_TITLE}`}
            error={title.length > MAX_TITLE}
            inputProps={{ 'data-testid': 'new-guide-title' }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={create} disabled={!valid || busy}>
          Create guide
        </Button>
      </DialogActions>
    </Dialog>
  );
}
